import { AVATAR_COLORS, MAX_BIO_LENGTH } from "@clubap/shared";
import { useEffect, useState } from "react";
import { deleteAccount, updateProfile, type ProfileUpdate } from "../api.js";
import { AcademicFields, type AcademicDraft } from "./AcademicFields.js";
import { AvatarPreview } from "./AvatarPreview.js";

export interface ProfileDraft {
  username: string;
  faculty: string | null;
  program: string | null;
  semester: number | null;
  bio: string | null;
  /** ÍNDICE en `AVATAR_COLORS`. Es lo que guarda la base y lo que viaja por el cable. */
  color: number;
}

interface Props {
  initial: ProfileDraft;
  /** Sólo cuando un admin edita a otra persona. Ausente = mi propia cuenta. */
  playerId?: string;
  onSaved: (update: ProfileUpdate) => void;
  onDeleted: () => void;
  onClose: () => void;
}

export function ProfilePanel({ initial, playerId, onSaved, onDeleted, onClose }: Props) {
  const [username, setUsername] = useState(initial.username);
  const [color, setColor] = useState(initial.color);
  const [bio, setBio] = useState(initial.bio ?? "");
  const [academic, setAcademic] = useState<AcademicDraft>({
    faculty: initial.faculty ?? "",
    program: initial.program ?? "",
    semester: initial.semester?.toString() ?? "",
  });

  /* Borrar vive detrás de un enlace y no de un botón: se abre el perfil para
     cambiar el semestre mil veces más que para irse. */
  const [dangerOpen, setDangerOpen] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const mine = playerId === undefined;

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const save = async () => {
    setBusy(true);
    setError(null);
    setSaved(false);

    try {
      const update = await updateProfile({
        ...(playerId ? { playerId } : {}),
        username,
        color,
        faculty: academic.faculty || null,
        program: academic.program || null,
        semester: academic.semester ? Number(academic.semester) : null,
        bio,
      });

      setSaved(true);
      onSaved(update);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "No se pudo guardar.");
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    setBusy(true);
    setError(null);

    try {
      await deleteAccount(playerId ? { playerId } : { password });
      onDeleted();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "No se pudo eliminar.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="overlay">
      {/*
        Dos columnas, la misma hoja que la tienda y el inventario.
        
        No es sólo parecido: es que las tres pantallas contestan la misma pregunta —qué
        aspecto tienes— y en dos de ellas ya se contestaba enseñándolo. El perfil era la
        única donde se cambiaba el aspecto a ciegas, en una lista de campos de texto.
      */}
      <div className="panel sheet sheet--probador">
        <div className="sheet__head">
          <h2 className="sheet__title">{mine ? "Mi perfil" : `Perfil de ${initial.username}`}</h2>
          <button className="icon-btn" type="button" onClick={onClose} aria-label="Cerrar perfil">
            ×
          </button>
        </div>

        {error && (
          <p className="sheet__error" role="alert">
            {error}
          </p>
        )}

        <div className="probador">
          <div className="probador__figura">
            <AvatarPreview worn={[]} color={color} />
            <p className="probador__ranura">Cómo te ven</p>

            {/*
              El color se elige tocándolo, no en un desplegable de nombres.

              Es lo único del perfil que se ve desde el otro lado de la sala, así que
              la figura de al lado cambia mientras se elige: la decisión se toma sobre
              el resultado y no sobre la palabra "turquesa".
            */}
            <div className="colores" role="radiogroup" aria-label="Color del caparazón">
              {AVATAR_COLORS.map((value, index) => (
                <button
                  key={index}
                  className={`colores__ficha${index === color ? " colores__ficha--on" : ""}`}
                  type="button"
                  role="radio"
                  aria-checked={index === color}
                  aria-label={`Color ${index + 1}`}
                  style={{
                    background: `#${value.toString(16).padStart(6, "0")}`,
                  }}
                  onClick={() => setColor(index)}
                />
              ))}
            </div>
          </div>

          <div className="probador__lista">
            <label className="auth__field">
              Nombre
              <input
                className="join__input"
                value={username}
                maxLength={16}
                onChange={(event) => setUsername(event.target.value)}
              />
            </label>

            <AcademicFields value={academic} onChange={setAcademic} />

            <label className="auth__field">
              Sobre ti
              {/*
                Un `textarea` de dos renglones y no un `input`: enseña de un vistazo
                cuánto cabe, que es lo que evita escribir un párrafo y descubrir el tope
                al guardar. El contador va al lado del rótulo por lo mismo.
              */}
              <textarea
                className="join__input probador__bio"
                value={bio}
                rows={2}
                maxLength={MAX_BIO_LENGTH}
                placeholder="Una línea sobre ti"
                onChange={(event) => setBio(event.target.value)}
              />
            </label>

            <p className="sheet__hint">
              Lo que pongas acá lo ve cualquiera que abra tu ficha. Puedes dejarlo en blanco.
            </p>
          </div>
        </div>

        <button
          className="btn profile__save"
          type="button"
          disabled={busy}
          onClick={() => void save()}
        >
          {busy ? "…" : "Guardar"}
        </button>

        {saved && <p className="sheet__hint">Guardado.</p>}

        {!dangerOpen && (
          <button
            className="profile__danger-link"
            type="button"
            onClick={() => setDangerOpen(true)}
          >
            {mine ? "Eliminar mi cuenta" : "Eliminar esta cuenta"}
          </button>
        )}

        {dangerOpen && (
          <div className="danger">
            <p className="danger__text">
              {mine
                ? "Perderás el acceso ahora mismo. Tu nombre queda reservado 30 días."
                : `Se elimina la cuenta de ${initial.username}. Su nombre queda reservado 30 días.`}
            </p>

            {mine && (
              <label className="auth__field">
                Tu contraseña
                <input
                  className="join__input"
                  type="password"
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                />
              </label>
            )}

            <div className="danger__row">
              <button
                className="btn btn--small"
                type="button"
                onClick={() => {
                  setDangerOpen(false);
                  setConfirming(false);
                }}
              >
                Mejor no
              </button>

              {!confirming ? (
                <button
                  className="btn btn--small btn--danger"
                  type="button"
                  disabled={mine && password.length === 0}
                  onClick={() => setConfirming(true)}
                >
                  Eliminar
                </button>
              ) : (
                <button
                  className="btn btn--small btn--danger"
                  type="button"
                  disabled={busy}
                  onClick={() => void remove()}
                >
                  Sí, estoy seguro
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
